import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SeatMapView } from '@bre-ticketing/seatmap';
import type { AvailabilityMap, ManifestGeometry, SeatGeometry } from '@bre-ticketing/seatmap/core';
import type { ApiError, CartView, PackageMapStep, WindowPackage } from '../api';
import { endpoints, when } from '../api';
import { Button, ErrorNote } from '../ui';
import { dateWords } from '../terms';

type Layout = ManifestGeometry & { seat_units: Record<string, string>; seat_zones: Record<string, string | null> };

/**
 * Picking a package's seats at the window (D-330).
 *
 * The window could only sell a package best available, so the person at the
 * counter who knew exactly where they wanted to sit for the whole season had to
 * be told no. This walks the same maps the storefront does: one per group of
 * performances that share seats, where one pick is the seat for all of them.
 * Picks are local until the last map; then the whole package is held at once,
 * so a half-picked package never sits in the cart.
 */
export function PackageSeats({ pkg, quantity, selections, onHeld, onCancel }: { pkg: WindowPackage; quantity: number; selections: string[]; onHeld: (cart: CartView) => void; onCancel: () => void }) {
  const steps = useQuery<{ steps: PackageMapStep[] }, ApiError>({
    queryKey: ['package-steps', pkg.code, selections.join(',')],
    queryFn: () => endpoints.packageSteps(pkg.code, selections),
  });
  const [stepIndex, setStepIndex] = useState(0);
  const [picks, setPicks] = useState<string[][]>([]);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const list = steps.data?.steps ?? [];
  const step = list[stepIndex] ?? null;
  const needed = step ? step.seats_per_package * quantity : 0;

  const detail = useQuery({ queryKey: ['performance', step?.performance_id], queryFn: () => endpoints.performance(step!.performance_id), enabled: Boolean(step) });
  // Seats outside the package's seating areas come back unavailable (D-384).
  const availability = useQuery({ queryKey: ['availability', step?.performance_id, pkg.code], queryFn: () => endpoints.availability(step!.performance_id, pkg.code), enabled: Boolean(step), refetchInterval: 5000 });
  const [layout, setLayout] = useState<Layout | null>(null);
  useEffect(() => {
    setLayout(null);
    if (!detail.data) return;
    fetch(detail.data.layout_url, { credentials: 'same-origin' })
      .then(async (r) => (r.ok ? ((await r.json()) as Layout) : null))
      .then(setLayout)
      .catch(() => setLayout(null));
  }, [detail.data]);

  const unitToSeat = useMemo(() => new Map(Object.entries(layout?.seat_units ?? {}).map(([seatId, unitId]) => [unitId, seatId])), [layout]);
  const availabilityMap = useMemo<AvailabilityMap>(() => {
    const out: Record<string, 'AVAILABLE' | 'HELD_BY_YOU' | 'UNAVAILABLE' | 'ACCESSIBLE_ON_REQUEST'> = {};
    for (const unit of availability.data?.units ?? []) {
      const seatId = unitToSeat.get(unit.unit_id);
      if (seatId) out[seatId] = unit.availability;
    }
    return out;
  }, [availability.data, unitToSeat]);

  const onSeatClick = (seat: SeatGeometry) => {
    setError(null);
    const next = new Set(chosen);
    if (next.has(seat.id)) next.delete(seat.id);
    else if (availabilityMap[seat.id] === 'AVAILABLE' || availabilityMap[seat.id] === 'ACCESSIBLE_ON_REQUEST') {
      if (next.size >= needed) {
        setError(`That is all ${needed} for this map. Tap a chosen seat to swap it.`);
        return;
      }
      next.add(seat.id);
    }
    setChosen(next);
  };

  const confirm = async () => {
    if (!layout || !step) return;
    const unitIds = [...chosen].map((seatId) => layout.seat_units[seatId]).filter((id): id is string => Boolean(id));
    const all = [...picks.slice(0, stepIndex), unitIds];
    if (stepIndex < list.length - 1) {
      setPicks(all);
      setChosen(new Set());
      setStepIndex(stepIndex + 1);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await endpoints.addPackage({ package: pkg.code, quantity, ...(selections.length > 0 ? { selections } : {}), seat: { mode: 'seats', unit_ids: all[0]!, groups: all } });
      onHeld(result.cart);
    } catch (cause) {
      const failure = cause as ApiError & { details?: { group?: number } };
      setError(failure.message);
      // A seat taken on an earlier map: go back to that map, keeping the picks before it.
      const group = failure.details?.group;
      if (typeof group === 'number' && group < all.length) {
        setPicks(all.slice(0, group));
        setChosen(new Set());
        setStepIndex(group);
      }
    } finally {
      setBusy(false);
    }
  };

  if (steps.isPending) return <p className="px-5 py-3 text-sm text-ink-400">Loading the maps…</p>;
  if (steps.isError) return <div className="px-5 py-3"><ErrorNote>{steps.error.message}</ErrorNote></div>;
  if (!step) {
    return (
      <div className="px-5 py-3 text-sm text-ink-600">
        {pkg.package_kind === 'CHOOSE_YOUR_OWN' && selections.length === 0 ? `Choose the ${dateWords().many} first, then pick seats.` : 'This package has no seat map to pick from; sell it best available.'}
        <Button className="ml-3" onClick={onCancel}>Back</Button>
      </div>
    );
  }
  return (
    <div className="space-y-2 px-5 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
        <p>
          <span className="font-semibold">
            Map {stepIndex + 1} of {list.length}: {step.event_title}
          </span>{' '}
          <span className="text-ink-600">
            {step.covers.length > 1 ? `the same seats for ${step.covers.length} ${dateWords().many}` : step.starts_at ? when(step.starts_at) : ''}
          </span>
        </p>
        <p className="tabular text-ink-600">
          {chosen.size} of {needed} chosen
        </p>
      </div>
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {layout ? (
        <SeatMapView layout={layout} mode="view" backgroundUrl={layout.manifest.background_url ?? null} availability={availabilityMap} selectedSeatIds={chosen} onSeatClick={onSeatClick} height={420} className="rounded-lg border border-line" />
      ) : (
        <p className="text-sm text-ink-400">Loading the seat map…</p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button variant="primary" className="touch" disabled={busy || chosen.size !== needed} onClick={() => void confirm()}>
          {busy ? 'Holding…' : stepIndex < list.length - 1 ? 'Next map' : 'Hold these seats'}
        </Button>
        <Button className="touch" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
